import React from 'react';

/**
 * Minimal Markdown renderer for the chat pane.
 *
 * Deliberately small — no dependency, no raw HTML, no link clicks.
 * Handles block-level: paragraphs, bullet lists, ordered lists, H1-H3, block quotes.
 * Handles inline: **bold**, *italic*, `code`.
 *
 * AI-generated text is untrusted: we render ONLY plain text nodes, never
 * set innerHTML, and ignore any HTML or link syntax in the source.
 */

type InlineToken =
  | { type: 'text'; text: string }
  | { type: 'bold'; text: string }
  | { type: 'italic'; text: string }
  | { type: 'code'; text: string };

function parseInline(raw: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  const re = /(\*\*([^*\n]+)\*\*|__([^_\n]+)__|\*([^*\n]+)\*|_([^_\n]+)_|`([^`\n]+)`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    if (m.index > last) tokens.push({ type: 'text', text: raw.slice(last, m.index) });
    if (m[2] != null) tokens.push({ type: 'bold', text: m[2] });
    else if (m[3] != null) tokens.push({ type: 'bold', text: m[3] });
    else if (m[4] != null) tokens.push({ type: 'italic', text: m[4] });
    else if (m[5] != null) tokens.push({ type: 'italic', text: m[5] });
    else if (m[6] != null) tokens.push({ type: 'code', text: m[6] });
    last = m.index + m[0].length;
  }
  if (last < raw.length) tokens.push({ type: 'text', text: raw.slice(last) });
  return tokens;
}

function renderInline(raw: string, keyBase: string): React.ReactNode[] {
  return parseInline(raw).map((t, i) => {
    const key = `${keyBase}-${i}`;
    switch (t.type) {
      case 'text':   return <React.Fragment key={key}>{t.text}</React.Fragment>;
      case 'bold':   return <strong key={key}>{t.text}</strong>;
      case 'italic': return <em key={key}>{t.text}</em>;
      case 'code':   return <code key={key} style={{ background: '#f1f3f5', padding: '1px 4px', borderRadius: 3, fontFamily: 'Consolas, monospace', fontSize: '0.9em' }}>{t.text}</code>;
    }
  });
}

interface Block {
  type: 'p' | 'ul' | 'ol' | 'h1' | 'h2' | 'h3' | 'quote' | 'table';
  lines: string[];
  /** For type 'table' only — parsed header cells + body row cells. */
  header?: string[];
  rows?: string[][];
}

// 2026-06-16 — Markdown pipe-table support. Discussion / Analysis mode often
// answers with a small comparison table (CY vs PY, pre vs post, scenario A vs
// B); rendering it as a real table instead of raw "| a | b |" text is the
// difference between readable and broken.
function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map(c => c.trim());
}

/** A GitHub-style delimiter row: every cell is dashes with optional colons. */
function isTableDelimiter(line: string): boolean {
  const t = line.trim();
  if (!t.includes('-') || !t.includes('|')) return false;
  const cells = splitTableRow(t);
  return cells.length > 0 && cells.every(c => /^:?-{1,}:?$/.test(c));
}

/** A plausible table row: contains a pipe and isn't a heading/list/quote. */
function looksLikeTableRow(trimmed: string): boolean {
  return trimmed.includes('|') && !/^[#>]/.test(trimmed);
}

// Exported for unit testing (the renderer itself needs a DOM; the block
// grouping — especially pipe-table detection — is pure and worth testing).
export function groupBlocks(source: string): Block[] {
  const rawLines = source.split(/\r?\n/);
  const blocks: Block[] = [];
  let current: Block | null = null;

  const push = () => { if (current) { blocks.push(current); current = null; } };

  for (let li = 0; li < rawLines.length; li++) {
    const line = rawLines[li];
    const trimmed = line.trim();
    if (!trimmed) { push(); continue; }

    // Table: a header row immediately followed by a delimiter row. Consume the
    // header, the delimiter, and all following pipe rows as one table block.
    if (
      looksLikeTableRow(trimmed) &&
      li + 1 < rawLines.length &&
      isTableDelimiter(rawLines[li + 1])
    ) {
      push();
      const header = splitTableRow(trimmed);
      const rows: string[][] = [];
      let j = li + 2;
      for (; j < rawLines.length; j++) {
        const rowTrim = rawLines[j].trim();
        if (!rowTrim || !looksLikeTableRow(rowTrim)) break;
        const cells = splitTableRow(rowTrim);
        // Normalise width to the header so ragged rows still render.
        while (cells.length < header.length) cells.push('');
        rows.push(cells.slice(0, header.length));
      }
      blocks.push({ type: 'table', lines: [], header, rows });
      li = j - 1;
      continue;
    }

    const h1 = trimmed.match(/^#\s+(.+)$/);
    const h2 = trimmed.match(/^##\s+(.+)$/);
    const h3 = trimmed.match(/^###\s+(.+)$/);
    const ul = trimmed.match(/^[-*]\s+(.+)$/);
    const ol = trimmed.match(/^\d+\.\s+(.+)$/);
    const quote = trimmed.match(/^>\s?(.*)$/);

    if (h3) { push(); blocks.push({ type: 'h3', lines: [h3[1]] }); continue; }
    if (h2) { push(); blocks.push({ type: 'h2', lines: [h2[1]] }); continue; }
    if (h1) { push(); blocks.push({ type: 'h1', lines: [h1[1]] }); continue; }
    if (ul) {
      if (!current || current.type !== 'ul') { push(); current = { type: 'ul', lines: [] }; }
      current.lines.push(ul[1]);
      continue;
    }
    if (ol) {
      if (!current || current.type !== 'ol') { push(); current = { type: 'ol', lines: [] }; }
      current.lines.push(ol[1]);
      continue;
    }
    if (quote) {
      if (!current || current.type !== 'quote') { push(); current = { type: 'quote', lines: [] }; }
      current.lines.push(quote[1]);
      continue;
    }
    if (!current || current.type !== 'p') { push(); current = { type: 'p', lines: [] }; }
    current.lines.push(trimmed);
  }
  push();
  return blocks;
}

export function Markdown({ source }: { source: string }) {
  const blocks = groupBlocks(source);
  return (
    <>
      {blocks.map((b, i) => {
        const key = `b-${i}`;
        switch (b.type) {
          case 'h1': return <div key={key} style={{ fontSize: 17, fontWeight: 700, margin: '8px 0 4px 0' }}>{renderInline(b.lines[0], key)}</div>;
          case 'h2': return <div key={key} style={{ fontSize: 15, fontWeight: 700, margin: '6px 0 4px 0' }}>{renderInline(b.lines[0], key)}</div>;
          case 'h3': return <div key={key} style={{ fontSize: 14, fontWeight: 600, margin: '6px 0 2px 0' }}>{renderInline(b.lines[0], key)}</div>;
          case 'ul':
            return (
              <ul key={key} style={{ margin: '4px 0 4px 0', paddingLeft: 18 }}>
                {b.lines.map((line, j) => <li key={`${key}-${j}`} style={{ margin: '2px 0' }}>{renderInline(line, `${key}-${j}`)}</li>)}
              </ul>
            );
          case 'ol':
            return (
              <ol key={key} style={{ margin: '4px 0 4px 0', paddingLeft: 20 }}>
                {b.lines.map((line, j) => <li key={`${key}-${j}`} style={{ margin: '2px 0' }}>{renderInline(line, `${key}-${j}`)}</li>)}
              </ol>
            );
          case 'quote':
            return (
              <blockquote key={key} style={{ margin: '6px 0', padding: '4px 10px', borderLeft: '3px solid #cbd5e0', color: '#4a5568', fontStyle: 'italic' }}>
                {b.lines.map((l, j) => <div key={`${key}-${j}`}>{renderInline(l, `${key}-${j}`)}</div>)}
              </blockquote>
            );
          case 'table': {
            const header = b.header ?? [];
            const rows = b.rows ?? [];
            return (
              <div key={key} style={{ overflowX: 'auto', margin: '8px 0' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 12.5, width: '100%' }}>
                  <thead>
                    <tr>
                      {header.map((h, j) => (
                        <th key={`${key}-h-${j}`} style={{ border: '1px solid #d8dee6', background: '#f1f5fa', padding: '4px 8px', textAlign: j === 0 ? 'left' : 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {renderInline(h, `${key}-h-${j}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, ri) => (
                      <tr key={`${key}-r-${ri}`} style={{ background: ri % 2 ? '#fafbfd' : '#fff' }}>
                        {r.map((c, ci) => (
                          <td key={`${key}-r-${ri}-${ci}`} style={{ border: '1px solid #e3e8ef', padding: '4px 8px', textAlign: ci === 0 ? 'left' : 'right', whiteSpace: 'nowrap' }}>
                            {renderInline(c, `${key}-r-${ri}-${ci}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          case 'p':
          default:
            return (
              <p key={key} style={{ margin: '4px 0', lineHeight: 1.45 }}>
                {b.lines.map((l, j) => (
                  <React.Fragment key={`${key}-${j}`}>
                    {renderInline(l, `${key}-${j}`)}
                    {j < b.lines.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </p>
            );
        }
      })}
    </>
  );
}
